import { useMemo } from 'react';
import { ReadBuyer } from '@tinct/core-api-models';
import {
  buyerLogoVariantKey,
  getBuyerLogoUrl,
} from '../../../utils/buyer-logo-url';
import logoExampleColor from '../assets/logo-example-color.png';
import logoExampleGreyscaleLight from '../assets/logo-example-greyscale-light.png';
import logoExampleGreyscaleDark from '../assets/logo-example-greyscale-dark.png';

export type UseBuyerLogoVariantOptions = {
  buyer: ReadBuyer | null;
  greyscale: boolean;
  dark: boolean;
};

export type UseBuyerLogoVariantReturn = {
  variantKey: ReturnType<typeof buyerLogoVariantKey>;
  currentLogoUrl: string | null;
  isReplacing: boolean;
  companyName: string;
  versionLabel: string;
  exampleLogo: string;
};

/**
 * Derives everything variant-specific (color/greyscale × light/dark) from the
 * block's display toggles: the PATCH key, the current logo (if any), the
 * human-readable version label and the matching example image.
 */
export const useBuyerLogoVariant = ({
  buyer,
  greyscale,
  dark,
}: UseBuyerLogoVariantOptions): UseBuyerLogoVariantReturn =>
  useMemo(() => {
    const currentLogoUrl = buyer
      ? getBuyerLogoUrl(buyer, { greyscale, dark })
      : null;
    return {
      variantKey: buyerLogoVariantKey({ greyscale, dark }),
      currentLogoUrl,
      isReplacing: !!currentLogoUrl,
      companyName: buyer?.company?.name ?? '',
      versionLabel: `${dark ? 'Dark' : 'Light'} theme, ${greyscale ? 'grayscale' : 'full color'}`,
      exampleLogo: greyscale
        ? dark
          ? logoExampleGreyscaleDark
          : logoExampleGreyscaleLight
        : logoExampleColor,
    };
  }, [buyer, greyscale, dark]);
